/* ═══════════════════════════════════════════════════════════════════════════
   RALJE — THE COSMOS
   Volume IV · MMXXVI
   ---------------------------------------------------------------------------
   A single full-viewport WebGL canvas that sits behind the entire homepage
   and paints the house's own night sky: ink, a slow nebula in Meenakari blue
   and Heirloom gold, and three parallax layers of stars.

   Deliberately dependency-free. No Three.js, no CDN, no build step. If WebGL
   is missing, blocked, or the context is lost, this file removes itself and
   the CSS fallback (a static ink gradient) takes over — the page is never
   left broken and never left bare.

   Palette is read from the section, which reads it from the Theme Customizer,
   so the sky can be retuned without touching this file.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var CFG = window.RaljeCosmosConfig || {};
  if (CFG.enabled === false) return;

  var root = document.documentElement;
  var reduced = false;
  try {
    reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  /* ── Palette ─────────────────────────────────────────────────────────── */

  function hex(v, fallback) {
    var m = /^#?([0-9a-f]{6})$/i.exec(String(v || '').trim());
    var n = parseInt(m ? m[1] : fallback, 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }

  var COL_INK   = hex(CFG.ink,   '0B0810');
  var COL_GOLD  = hex(CFG.gold,  'B8893E');
  var COL_DEEP  = hex(CFG.deep,  '24325C');
  var COL_EMBER = hex(CFG.ember, '7A1F1F');

  var INTENSITY = clamp(num(CFG.intensity, 100) / 100, 0, 2);
  var STARS     = clamp(num(CFG.stars, 100) / 100, 0, 2);
  var DRIFT     = clamp(num(CFG.drift, 100) / 100, 0, 3);
  var PARALLAX  = clamp(num(CFG.parallax, 100) / 100, 0, 3);

  function num(v, d) { var n = parseFloat(v); return isFinite(n) ? n : d; }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* ── Canvas ──────────────────────────────────────────────────────────── */

  var canvas = document.createElement('canvas');
  canvas.className = 'ralje-cosmos__canvas';
  canvas.setAttribute('aria-hidden', 'true');

  var gl = null;
  try {
    var opts = {
      alpha: false, antialias: false, depth: false, stencil: false,
      premultipliedAlpha: false, preserveDrawingBuffer: false,
      powerPreference: 'low-power', failIfMajorPerformanceCaveat: false
    };
    gl = canvas.getContext('webgl', opts) || canvas.getContext('experimental-webgl', opts);
  } catch (e) { gl = null; }

  if (!gl) { root.classList.add('ralje-cosmos--static'); return; }

  /* ── Shaders ─────────────────────────────────────────────────────────── */

  var VERT = [
    'attribute vec2 aPos;',
    'void main(){ gl_Position = vec4(aPos, 0.0, 1.0); }'
  ].join('\n');

  var FRAG = [
    'precision highp float;',

    'uniform vec2  uRes;',
    'uniform float uTime;',
    'uniform vec2  uPan;',
    'uniform float uIntensity;',
    'uniform float uStars;',
    'uniform vec3  uInk;',
    'uniform vec3  uGold;',
    'uniform vec3  uDeep;',
    'uniform vec3  uEmber;',
    'uniform float uOctaves;',

    'float hash21(vec2 p){',
    '  p = fract(p * vec2(123.34, 456.21));',
    '  p += dot(p, p + 45.32);',
    '  return fract(p.x * p.y);',
    '}',

    'float vnoise(vec2 p){',
    '  vec2 i = floor(p), f = fract(p);',
    '  vec2 u = f * f * (3.0 - 2.0 * f);',
    '  float a = hash21(i);',
    '  float b = hash21(i + vec2(1.0, 0.0));',
    '  float c = hash21(i + vec2(0.0, 1.0));',
    '  float d = hash21(i + vec2(1.0, 1.0));',
    '  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);',
    '}',

    // Rotated, scaled octaves. Bound is constant (GLSL ES 1.0); uOctaves
    // lets weaker devices bail out early without recompiling the program.
    'float fbm(vec2 p){',
    '  float v = 0.0, a = 0.5;',
    '  mat2 m = mat2(1.62, 1.18, -1.18, 1.62);',
    '  for (int i = 0; i < 5; i++){',
    '    if (float(i) >= uOctaves) break;',
    '    v += a * vnoise(p);',
    '    p = m * p;',
    '    a *= 0.5;',
    '  }',
    '  return v;',
    '}',

    // One parallax plane of stars. Cells are hashed; only cells above the
    // threshold carry a star, so density is (1.0 - thresh).
    'float starfield(vec2 uv, float scale, float thresh, float t, float size){',
    '  vec2 g = uv * scale;',
    '  vec2 id = floor(g), f = fract(g);',
    '  float h = hash21(id);',
    '  if (h < thresh) return 0.0;',
    '  vec2 c = vec2(hash21(id + 1.37), hash21(id + 7.71));',
    '  float d = length(f - c);',
    '  float tw = 0.62 + 0.38 * sin(t * 1.7 + h * 44.0);',
    '  float core = smoothstep(size, 0.0, d) * tw;',
    '  float halo = smoothstep(size * 5.0, 0.0, d) * 0.10 * tw;',
    '  return core + halo;',
    '}',

    'void main(){',
    '  vec2 frag = gl_FragCoord.xy;',
    '  vec2 uv = (frag - 0.5 * uRes) / max(uRes.y, 1.0);',
    '  float t = uTime;',

    // ── Nebula ──
    '  vec2 np = uv * 1.05 + uPan * 0.05;',
    '  float n1 = fbm(np * 1.55 + vec2( t * 0.013, -t * 0.009));',
    '  float n2 = fbm(np * 2.85 + vec2(-t * 0.019,  t * 0.012) + n1 * 0.85);',
    '  float cloud = smoothstep(0.26, 0.97, n1 * 0.68 + n2 * 0.52);',
    '  float wisp  = smoothstep(0.52, 1.00, n2);',

    // A high, off-centre forge glow — the light source of the house.
    '  float core = smoothstep(1.05, 0.0, length((uv - vec2(-0.06, 0.20)) * vec2(0.78, 1.0)));',

    '  vec3 col = uInk;',
    '  col = mix(col, uDeep,  cloud * 0.56 * uIntensity);',
    '  col += uGold  * pow(cloud, 2.05) * 0.30 * uIntensity;',
    '  col += uEmber * pow(wisp,  2.80) * 0.15 * uIntensity;',
    '  col += uGold  * core * 0.055 * uIntensity;',

    // ── Stars, three planes at different parallax rates ──
    '  float s = 0.0;',
    '  s += starfield(uv + uPan * 0.020, 13.0, 0.880, t * 0.75, 0.055) * 0.42;',
    '  s += starfield(uv + uPan * 0.052, 25.0, 0.905, t * 1.05, 0.042) * 0.70;',
    '  s += starfield(uv + uPan * 0.098, 43.0, 0.930, t * 0.85, 0.032) * 1.00;',
    '  col += vec3(0.97, 0.95, 0.90) * s * uStars * 0.9;',

    // ── Vignette, so the type always holds ──
    '  float vig = 1.0 - 0.42 * smoothstep(0.50, 1.45, length(uv * vec2(0.82, 1.0)));',
    '  col *= vig;',

    // ── Dither, to kill banding across the large soft gradients ──
    '  float g = (hash21(frag + fract(t) * 137.0) - 0.5) * 0.016;',
    '  col += g;',

    '  gl_FragColor = vec4(max(col, 0.0), 1.0);',
    '}'
  ].join('\n');

  function compile(type, src) {
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  var vs = compile(gl.VERTEX_SHADER, VERT);
  var fs = compile(gl.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) { root.classList.add('ralje-cosmos--static'); return; }

  var prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    root.classList.add('ralje-cosmos--static');
    return;
  }
  gl.useProgram(prog);

  var buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  var aPos = gl.getAttribLocation(prog, 'aPos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  function U(n) { return gl.getUniformLocation(prog, n); }
  var uRes = U('uRes'), uTime = U('uTime'), uPan = U('uPan');
  var uIntensity = U('uIntensity'), uStars = U('uStars'), uOctaves = U('uOctaves');

  gl.uniform3fv(U('uInk'),   COL_INK);
  gl.uniform3fv(U('uGold'),  COL_GOLD);
  gl.uniform3fv(U('uDeep'),  COL_DEEP);
  gl.uniform3fv(U('uEmber'), COL_EMBER);
  gl.uniform1f(uIntensity, INTENSITY);
  gl.uniform1f(uStars, STARS);

  /* ── Quality ─────────────────────────────────────────────────────────── */

  var narrow  = Math.min(window.innerWidth, window.innerHeight) < 760;
  var scale   = narrow ? 0.62 : 0.82;   // render below CSS size; the sky is soft
  var dprCap  = narrow ? 1.0 : 1.5;
  var octaves = narrow ? 3.0 : 5.0;
  gl.uniform1f(uOctaves, octaves);

  var W = 0, H = 0;

  function resize() {
    var dpr = Math.min(window.devicePixelRatio || 1, dprCap);
    var cw = Math.max(1, Math.floor(window.innerWidth  * dpr * scale));
    var ch = Math.max(1, Math.floor(window.innerHeight * dpr * scale));
    if (cw === W && ch === H) return;
    W = cw; H = ch;
    canvas.width = W;
    canvas.height = H;
    gl.viewport(0, 0, W, H);
    gl.uniform2f(uRes, W, H);
    dirty = true;
  }

  /* ── Parallax ────────────────────────────────────────────────────────── */

  var panX = 0, panY = 0, tgtX = 0, tgtY = 0;

  function onScroll() {
    var vh = window.innerHeight || 1;
    tgtY = (window.pageYOffset || root.scrollTop || 0) / vh * 0.55 * PARALLAX;
  }

  function onPointer(e) {
    var w = window.innerWidth || 1, h = window.innerHeight || 1;
    var px = (e.clientX != null ? e.clientX : w / 2) / w - 0.5;
    var py = (e.clientY != null ? e.clientY : h / 2) / h - 0.5;
    tgtX = px * 0.20 * PARALLAX;
    pointerY = py * 0.10 * PARALLAX;
  }

  var pointerY = 0;

  /* ── Loop ────────────────────────────────────────────────────────────── */

  var start = (window.performance && performance.now ? performance.now() : Date.now());
  var running = false, raf = 0, dirty = true;
  var slowFrames = 0, degraded = false, last = 0;

  function frame(now) {
    raf = 0;
    if (!running) return;

    var dt = last ? now - last : 16;
    last = now;

    // Two seconds of sustained sub-30fps and we quietly step the sky down
    // rather than sit on a stuttering page.
    if (!degraded) {
      if (dt > 33) { slowFrames++; } else if (slowFrames > 0) { slowFrames--; }
      if (slowFrames > 90) {
        degraded = true;
        scale = Math.max(0.42, scale * 0.7);
        dprCap = 1.0;
        octaves = 3.0;
        gl.uniform1f(uOctaves, octaves);
        W = H = 0;
        resize();
      }
    }

    var e = 0.075;
    panX += (tgtX - panX) * e;
    panY += ((tgtY + pointerY) - panY) * e;

    var t = reduced ? 0 : ((now - start) / 1000) * DRIFT;
    gl.uniform1f(uTime, t);
    gl.uniform2f(uPan, panX, -panY);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    if (reduced) {
      // Static sky: redraw only when something actually moved.
      var moved = Math.abs(tgtX - panX) > 0.0004 || Math.abs((tgtY + pointerY) - panY) > 0.0004;
      if (!moved && !dirty) { running = false; return; }
      dirty = false;
    }
    raf = requestAnimationFrame(frame);
  }

  function play() {
    if (running || document.hidden) return;
    running = true;
    last = 0;
    if (!raf) raf = requestAnimationFrame(frame);
  }

  function pause() {
    running = false;
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
  }

  /* ── Wiring ──────────────────────────────────────────────────────────── */

  function mount() {
    if (!canvas.parentNode) document.body.insertBefore(canvas, document.body.firstChild);
    root.classList.add('ralje-cosmos--live');
    resize();
    onScroll();
    panY = tgtY;
    play();
  }

  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount, { once: true });

  window.addEventListener('resize', function () { resize(); play(); }, { passive: true });
  window.addEventListener('orientationchange', function () { setTimeout(function () { resize(); play(); }, 250); }, { passive: true });
  window.addEventListener('scroll', function () { onScroll(); play(); }, { passive: true });
  window.addEventListener('pointermove', function (e) { onPointer(e); play(); }, { passive: true });
  document.addEventListener('visibilitychange', function () { document.hidden ? pause() : play(); });

  canvas.addEventListener('webglcontextlost', function (e) {
    e.preventDefault();
    pause();
    root.classList.remove('ralje-cosmos--live');
    root.classList.add('ralje-cosmos--static');
  }, false);

  window.RaljeCosmos = { play: play, pause: pause, canvas: canvas };
})();